import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';

import { useAssignRoleMutation, useGetWorkspaceRolesQuery, useValidateQuery } from 'src/apiClients';
import { ROLE_OPTIONS, type RoleType, type WorkspaceRole } from 'src/apiClients/queries';

import { ExpandedPanel } from './ExpandedPanel';
import { i18n } from './WorkspacesSection.i18n';

const GRID_COLUMNS = 'minmax(160px, 2fr) 160px';

const useStyles = makeStyles()((theme) => ({
    selectCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    select: {
        fontSize: '0.875rem',
        '& .MuiSelect-select': {
            padding: theme.spacing(0.5, 1),
        },
    },
}));

interface WorkspaceUsersProps {
    workspaceId: string;
    tenantId: string;
}

export const WorkspaceUsers = ({ workspaceId, tenantId }: WorkspaceUsersProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [savingUserId, setSavingUserId] = useState<string | null>(null);

    const { data: validateData } = useValidateQuery(undefined);
    const currentUserId = validateData?.uid;

    const {
        data: roles,
        isLoading,
        isError,
        refetch,
    } = useGetWorkspaceRolesQuery({
        workspaceId,
        tenantId,
    });

    const [assignRole] = useAssignRoleMutation();

    const sortedRoles = useMemo(() => {
        if (!roles) return [];
        return [...roles].sort((a, b) => a.login.localeCompare(b.login));
    }, [roles]);

    const roleLabel = useCallback(
        (roleType: string) => {
            switch (roleType) {
                case 'O':
                    return intl.formatMessage(i18n.roleOwner);
                case 'C':
                    return intl.formatMessage(i18n.roleContributor);
                case 'V':
                    return intl.formatMessage(i18n.roleViewer);
                default:
                    return roleType;
            }
        },
        [intl],
    );

    const handlePermissionChange = useCallback(
        async (role: WorkspaceRole, event: SelectChangeEvent) => {
            const newRoleType = event.target.value as RoleType;
            if (newRoleType === role.roleType) return;

            setSavingUserId(role.uid);
            try {
                await assignRole({
                    workspaceId,
                    tenantId,
                    userId: role.uid,
                    roleType: newRoleType,
                }).unwrap();
                refetch();
            } catch {
                enqueueSnackbar(intl.formatMessage(i18n.permissionUpdateFailed), {
                    variant: 'error',
                });
            } finally {
                setSavingUserId(null);
            }
        },
        [assignRole, workspaceId, tenantId, refetch, enqueueSnackbar, intl],
    );

    const columnHeaders = useMemo(
        () => [intl.formatMessage(i18n.columnAccount), intl.formatMessage(i18n.columnPermission)],
        [intl],
    );

    return (
        <ExpandedPanel
            title={intl.formatMessage(i18n.usersTitle)}
            columnHeaders={columnHeaders}
            gridColumns={GRID_COLUMNS}
            isLoading={isLoading}
            isError={!!isError}
            isEmpty={sortedRoles.length === 0}
            loadingMessage={intl.formatMessage(i18n.loadingUsers)}
            errorMessage={intl.formatMessage(i18n.usersLoadError)}
            emptyMessage={intl.formatMessage(i18n.noUsers)}
        >
            {(panelClasses) =>
                sortedRoles.map((role) => {
                    const isSaving = savingUserId === role.uid;

                    const isSelf = role.uid === currentUserId;

                    return (
                        <Box key={role.uid} className={panelClasses.row}>
                            <Typography
                                variant="body2"
                                className={panelClasses.cellText}
                                title={role.login}
                            >
                                {role.login}
                            </Typography>
                            <Box className={classes.selectCell}>
                                <Tooltip
                                    title={
                                        isSelf
                                            ? intl.formatMessage(i18n.cannotChangeOwnPermission)
                                            : ''
                                    }
                                    placement="top"
                                >
                                    <span>
                                        <Select
                                            value={role.roleType}
                                            onChange={(e) => handlePermissionChange(role, e)}
                                            size="small"
                                            variant="outlined"
                                            className={classes.select}
                                            disabled={isSaving || isSelf}
                                        >
                                            {ROLE_OPTIONS.map((opt) => (
                                                <MenuItem key={opt} value={opt}>
                                                    {roleLabel(opt)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </span>
                                </Tooltip>
                                {isSaving && <CircularProgress size={14} />}
                            </Box>
                        </Box>
                    );
                })
            }
        </ExpandedPanel>
    );
};
